// @flow
import React, { useState } from "react";
import { useAsync } from "hooks";
import { fetchItemFlows } from "data/api";
import { isListed } from "data/utils";
import Status from "components/Status";
import ItemFlows from "views/itemFlows";
import Constants from "data/constants";
import {
    type ItemFlowsConfigDTO,
    type ItemFlowDirectionEnum,
    type NewItemResultDTO,
} from "data/types";

type Props = {
    /** current folder's itemUri  */
    itemUri: string,
    /** callback when user closes */
    onClose: () => void,
    /** flow direction "in" vs "out" */
    direction?: ItemFlowDirectionEnum,
    /** optional flow.id to pre-select */
    flowId?: string,
    /** flow.ids to allow visible */
    visibleFlowIds?: Array<string>,
    /** optional category.id to pre-select */
    categoryId?: string,
    /** optional default attribute values to pass to form */
    defaultValues?: Object,
    /** optional whether to preview the newly created item
     * @default true
     */
    previewNewItem?: boolean,
    /** optional callback when completed
     * @param {NewItemResultDTO} data details about the item created
     */
    onDone?: (data: NewItemResultDTO) => void,
};

const ItemFlowsContainer = (props: Props) => {
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    // get input item flows
    const state = useAsync(async () => {
        try {
            const cfg: ItemFlowsConfigDTO = await fetchItemFlows(
                props.itemUri,
                props.direction
            );
            // filter requested visible flows
            // PS: yes I know we could filter them on the server-side, but it would not improve performance just reduce packet size
            if (props.visibleFlowIds) {
                cfg.flows = cfg.flows.filter((f) =>
                    isListed(f.id, props.visibleFlowIds)
                );
            }
            // pre-select a specific flow
            if (props.flowId) {
                setSelectedFlow(cfg.flows.find((f) => f.id === props.flowId));
            }
            // pre-select a specific category
            if (props.categoryId) {
                setSelectedCategory(
                    cfg.categories.find((c) => c.id === props.categoryId)
                );
            }
            return cfg;
        } catch (err) {
            console.warn("Error while fetchItemFlows", err);
            throw err;
        }
    }, [props.itemUri]);

    if (state.loading) return <Status status={Constants.LOADING} />;
    if (state.error) return <Status status={Constants.ERROR} />;

    return (
        <ItemFlows
            asModal
            flows={state.value.flows}
            categories={state.value.categories}
            selectedFlow={selectedFlow}
            selectedCategory={selectedCategory}
            itemUri={props.itemUri}
            onClose={props.onClose}
            defaultValues={props.defaultValues}
            previewNewItem={props.previewNewItem}
            onDone={props.onDone}
        />
    );
};
ItemFlowsContainer.displayName = "ItemFlowsContainer";
ItemFlowsContainer.defaultProps = {
    direction: "in",
};
export default ItemFlowsContainer;
